import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiError, type ProfileView } from "@keydials/api-client";
import { HELP_ANCHORS } from "@keydials/ui";
import { api, getAccessToken, useAuth } from "../auth/AuthContext";
import { useTheme } from "../theme/ThemeProvider";
import { Icon } from "../components/Icon";
import { PinSetupModal } from "../components/chrome/PinSetupModal";
import { SettingsCard as Card } from "../components/SettingsCard";
import { SubscriptionCard } from "../components/SubscriptionCard";
import { OrganisationNameCard } from "../components/OrganisationNameCard";
import { MiloCard } from "../components/MiloCard";
import { ReferralLinksCard } from "../components/ReferralLinksCard";
import { ConnectedCalendarsCard } from "../components/ConnectedCalendarsCard";
import { CalendarFeedCard } from "../components/CalendarFeedCard";
import { lastPlace } from "../lib/lastPlace";
import { draftFrom, isDirty, textsAboutToGoQuiet, toRequest, type ProfileDraft } from "./accountDraft";

/**
 * Your own account — the page behind the avatar menu's Settings entry.
 *
 * Until now that entry did nothing at all: it closed the menu and went nowhere, because there was no
 * per-user settings surface to send it to. Theme and the shared-device PIN were loose items in the menu
 * beside it, and the one thing people actually needed to set — a phone number — could not be entered
 * anywhere in the product, which is why the SMS notification channel has never been reachable by anyone.
 *
 * Deliberately about YOU, not about a dial: what a dial does is on that dial's own Settings tab. The two
 * are different questions and were worth keeping apart, even though both are called "settings".
 */

const field: React.CSSProperties = {
  font: "inherit",
  fontSize: 14,
  padding: "8px 11px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface)",
  color: "var(--text)",
  width: "100%",
  maxWidth: 380,
};

const label: React.CSSProperties = {
  display: "block",
  fontSize: 12.5,
  fontWeight: 600,
  color: "var(--text-muted)",
  marginBottom: 5,
};

export function AccountSettings() {
  const navigate = useNavigate();
  const location = useLocation();
  // KEY-768: with no page of ours behind this one — opened fresh, or back from connecting Google or Outlook, whose
  // sign-in pages are what the browser's history holds — Back goes to where the person was, never into those pages.
  const goBack = () => (location.key === "default" ? navigate(lastPlace() ?? "/instances", { replace: true }) : navigate(-1));
  const { me, refreshMe } = useAuth();
  const { theme, themes, setTheme } = useTheme();

  const [profile, setProfile] = useState<ProfileView | null>(null);
  const [draft, setDraft] = useState<ProfileDraft>({ displayName: "", phoneNumber: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);

  const load = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      return;
    }
    try {
      const view = await api.getProfile(token);
      setProfile(view);
      setDraft(draftFrom(view));
    } catch {
      setError("Couldn't load your account details.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // /settings#calendars (the Connect your calendar guide, a dial's welcome page) lands on Connected calendars.
  // The card loads its own data, so it can arrive a beat after the page — look for it for a few seconds.
  useEffect(() => {
    const id = location.hash.replace(/^#/, "");
    if (!id) return;
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ block: "start", behavior: "smooth" });
        clearInterval(timer);
      } else if (++tries > 25) {
        clearInterval(timer);
      }
    }, 120);
    return () => clearInterval(timer);
  }, [location.hash]);

  const dirty = isDirty(profile, draft);
  // Removing a number silences the texts hanging off it, and the server clears those subscriptions rather
  // than leaving them switched on and undeliverable. Worth saying before they press Save, not after.
  const losingTexts = textsAboutToGoQuiet(profile, draft);

  async function save() {
    const token = getAccessToken();
    if (!token) {
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await api.updateProfile(token, toRequest(draft));
      setSaved(true);
      await load();
      // The header greeting and avatar read the display name off /api/me, and the SMS channel gate reads
      // the phone number off it, so neither would notice this save without re-fetching.
      await refreshMe();
    } catch (e) {
      setError(e instanceof ApiError && e.message ? e.message : "Couldn't save your details.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "26px 28px 80px", display: "flex", flexDirection: "column", gap: "var(--gap-stack)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={goBack}
          className="kd-btn kd-btn-sm"
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <Icon name="back" size={15} />
          Back
        </button>
        <h1 style={{ margin: 0, fontSize: 19, fontWeight: 800, fontFamily: "var(--font-display)", color: "var(--text)" }}>
          Your settings
        </h1>
      </div>

      <Card title="Your details" hint="How you appear to everyone else, and where your texts go.">
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <span style={label}>Email</span>
            <div style={{ ...field, background: "var(--surface-inset)", color: "var(--text-muted)" }}>
              {profile?.email ?? me?.email ?? "—"}
            </div>
            <p style={{ margin: "6px 0 0", fontSize: 12.5, color: "var(--text-faint)" }}>
              This is how you sign in, so it can't be changed here.
            </p>
          </div>

          <div>
            <label style={label} htmlFor="kd-display-name">Name</label>
            <input
              id="kd-display-name"
              style={field}
              value={draft.displayName}
              maxLength={200}
              placeholder="Leave empty to show your email instead"
              onChange={(e) => setDraft((d) => ({ ...d, displayName: e.target.value }))}
            />
          </div>

          <div>
            <label style={label} htmlFor="kd-phone">Mobile number</label>
            <input
              id="kd-phone"
              style={field}
              value={draft.phoneNumber}
              maxLength={40}
              placeholder="e.g. 07700 900123"
              data-help-anchor={HELP_ANCHORS.accountPhone}
              onChange={(e) => setDraft((d) => ({ ...d, phoneNumber: e.target.value }))}
            />
            <p style={{ margin: "6px 0 0", fontSize: 12.5, lineHeight: 1.55, color: "var(--text-faint)" }}>
              Needed before a dial can text you. Without one, the text option isn't offered when you
              subscribe to a dial — write it however you'd dial it; it's stored exactly as you type it.
            </p>
          </div>

          {losingTexts > 0 && (
            <p role="status" style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: "var(--st-amber, #b45309)" }}>
              Removing your number will also switch off{" "}
              <strong>{losingTexts} text {losingTexts === 1 ? "subscription" : "subscriptions"}</strong>. Email
              and in-app notifications aren't affected.
            </p>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button className="kd-btn-primary kd-btn-sm" onClick={() => void save()} disabled={!dirty || busy}>
              {busy ? "Saving…" : "Save"}
            </button>
            {saved && !dirty && <span style={{ fontSize: 13, color: "var(--text-muted)" }}>Saved.</span>}
          </div>

          {error && <div role="alert" style={{ fontSize: 13, color: "var(--st-overdue)" }}>{error}</div>}
        </div>
      </Card>

      <Card title="Appearance" hint="Your choice only — it doesn't change what anyone else sees, and it follows you to any device you sign in on.">
        {/* KEY-755: read from the theme registry, so a new theme appears here with no change to this page. */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {themes.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setTheme(option.id)}
              aria-pressed={theme === option.id}
              title={option.description}
              className="kd-focusable"
              style={{
                display: "inline-flex", alignItems: "center", gap: 7, font: "inherit", fontSize: 13, fontWeight: 600,
                padding: "8px 14px", borderRadius: "var(--radius-sm)", cursor: "pointer",
                border: `var(--border-w) solid ${theme === option.id ? "var(--accent-border)" : "var(--border)"}`,
                background: theme === option.id ? "var(--accent-soft)" : "var(--surface)",
                color: theme === option.id ? "var(--accent-text)" : "var(--text-muted)",
              }}
            >
              <Icon name={option.icon} size={15} />
              {option.label}
            </button>
          ))}
        </div>
      </Card>

      {/* KEY-743: the organisation's name, for whoever may edit it (org.edit) — renders nothing for anyone else.
          The dial page asks a self-serve buyer once; this is where it can be changed any time. */}
      <OrganisationNameCard />

      {/* The organisation's plan (KEY-635) — for whoever holds billing.read, which is its administrator.
          Members see nothing here: the plan is not theirs to act on. */}
      {me?.organisationId && me.capabilities.includes("billing.read") && (
        <SubscriptionCard organisationId={me.organisationId} email={me.email} />
      )}

      {/* Milo's tier on the plan (KEY-716), for the same person. Renders nothing where there is no ladder to pick from. */}
      {me?.organisationId && me.capabilities.includes("billing.read") && (
        <MiloCard organisationId={me.organisationId} />
      )}

      {/* An affiliate's own code and link (KEY-514). Renders nothing for anybody who isn't one. */}
      <ReferralLinksCard />

      {/* KEY-736: the person's own calendars, for the dials that show a diary. Renders nothing when the feature is off. */}
      <ConnectedCalendarsCard />
      <CalendarFeedCard />

      <Card
        title="Shared-device PIN"
        hint="Unlocks a wall tablet that has gone to its lock screen. Your PIN is yours — a shared one would erase the record of who changed what."
      >
        <div>
          <button className="kd-btn kd-btn-sm" onClick={() => setPinOpen(true)}>
            Set up your PIN
          </button>
        </div>
      </Card>

      {pinOpen && <PinSetupModal onClose={() => setPinOpen(false)} />}
    </div>
  );
}
